import {
  makeObservable,
  observable,
  action,
  computed,
  IObservableArray,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { SessionEventKind, SessionEventModelData, SessionMessageEventKind } from "./types";
import { SessionEventObservable } from "./SessionEventObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { liveQuery, Subscription } from "dexie";
import { JsonObject, Maybe } from "@/domains/common/types";
import { CreateSessionEventOperation } from "@/store/sync/operations/sessions/CreateSessionEventOperation";
import { uuidModule } from "@/modules/uuid";
import { Uuid } from "@/domains/global/identifiers";
import { InputFormKind } from "@/components/active-session/InputRequestForms";
import { CreateMessageSentSessionEventOperation } from "@/store/sync/operations/events/CreateMessageSentEventOperation";
export class AppStoreSessionEventStore extends BaseSyncModelStore<
  SessionEventObservable,
  SessionEventModelData
> {
  sessionEvents: IObservableArray<SessionEventObservable> = observable.array([]);
  liveQuerySubscription: Maybe<Subscription>;
  private initialLoaded: boolean = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SessionEvent, ...injectedDeps });
    makeObservable<
      this,
      | "liveQuerySubscription"
      | "subscribeLiveQuery"
      | "unsubscribeLiveQuery"
      | "setIsReady"
      | "initialLoaded"
    >(this, {
      createMessageSentSessionEvent: true,
      getSessionEventsBySessionId: true,
      createSyncModel: false,
      liveQuerySubscription: observable,
      sessionEvents: observable,
      initialLoaded: observable,

      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,
      setIsReady: action,
      createSessionEvent: action,
      createInputRequestSessionEvent: action,
      createInputResponseSessionEvent: action,

      isReady: computed,
      allSessionEvents: computed,
    });

    onBecomeObserved(this, "sessionEvents", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "sessionEvents", () => this.unsubscribeLiveQuery());
  }

  get isReady() {
    return this.initialLoaded;
  }

  get allSessionEvents(): SessionEventObservable[] {
    return this.sessionEvents;
  }

  createSyncModel(data: SyncUpdateValue<SessionEventModelData>): SessionEventObservable {
    return new SessionEventObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  private subscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      // Get all session event IDs from the local Dexie table
      return this.localTable.toCollection().primaryKeys();
    }).subscribe({
      next: async ids => {
        const sessionEvents = await this.getManyAsync(ids as string[]);
        runInAction(() => {
          this.sessionEvents.replace(sessionEvents);
          this.setIsReady(true);
        });
      },
    });
  }

  private unsubscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
  }

  private setIsReady(value: boolean) {
    this.initialLoaded = value;
  }

  /**
   * Returns an observable array of session events for a given sessionId
   */
  getSessionEventsBySessionId = (sessionId: string): SessionEventObservable[] => {
    if (typeof sessionId !== "string") return [];
    return this.sessionEvents.filter(event => event.sessionId === sessionId);
  };

  /**
   * Creates a session event by executing the create session event sync operation.
   */
  public async createSessionEvent({
    session_id,
    event_kind,
    event_value,
  }: {
    session_id: string;
    event_kind: SessionEventKind;
    event_value: { kind: string } & Record<string, unknown>;
  }): Promise<void> {
    const id = uuidModule.generate();
    return new CreateSessionEventOperation({
      store: this.store,
      payload: {
        id,
        session_id,
        event_kind,
        value_json: event_value,
        locally_created_at: new Date().toISOString(),
      },
    }).execute();
  }

  public async createMessageSentSessionEvent({
    session_id,
    message_value,
  }: {
    session_id: string;
    message_value: { kind: SessionEventKind | SessionMessageEventKind } & Record<string, unknown>;
  }): Promise<void> {
    const id = uuidModule.generate();
    return new CreateMessageSentSessionEventOperation({
      store: this.store,
      payload: {
        id,
        session_id,
        event_kind: "MESSAGE_SENT",
        message_value_json: message_value,
        locally_created_at: new Date().toISOString(),
      },
    }).execute();
  }

  public async createInputRequestSessionEvent({
    sessionId,
    metadata,
    form,
  }: {
    sessionId: string;
    metadata: JsonObject;
    form: InputFormKind;
  }): Promise<void> {
    await this.createSessionEvent({
      session_id: sessionId,
      event_kind: "INPUT_REQUESTED",
      event_value: {
        kind: "INPUT_REQUESTED",
        id: uuidModule.generate(),
        form,
        metadata,
      },
    });
  }

  public async createInputResponseSessionEvent({
    sessionId,
    config,
    requestId,
    metadata,
    inputKind,
  }: {
    sessionId: string;
    config: JsonObject;
    requestId: Uuid;
    metadata: Record<string, unknown>;
    inputKind: string;
  }): Promise<void> {
    // Only serialize metadata which might contain complex objects
    const serializableMetadata = JSON.parse(JSON.stringify(metadata));

    return this.createSessionEvent({
      session_id: sessionId,
      event_kind: "INPUT_PROVIDED",
      event_value: {
        kind: "INPUT_PROVIDED",
        session_id: sessionId,
        value: config,
        request_id: requestId,
        input_kind: inputKind,
        metadata: serializableMetadata,
      },
    });
  }
}
